/**
 * Fast draft of some poorly assembled thoughts on how we might clean up / systematize the use of temporary state in our forms.
 *
 * The idea is to use a "local" copy in the page component that is connected to continuous validation via zod and "dirty" tracking based
 * on the deltas from immer updates.
 *
 * The hope is that by using "paths" such as those used in zod's errors or lodash's get/set we can have hooks and components that can use these
 * pseudo-models for dirty/validation and give us some repeatable patterns for form handling.
 *
 * This is loosely based on the hooks / systems seen in react-forms and is only a rough draft - this will need more careful thought and testing.
 */
import { produce, Draft } from 'immer';
import { useState, useCallback } from 'react';

type Updater<TState> = (cb: (draft: Draft<TState>) => void) => void;
export function useTemporaryState<TState extends Record<string, unknown>>(
  defaultValue: TState
) {
  const [state, setState] = useState(defaultValue);
  const clearState = useCallback(() => {
    setState(defaultValue);
  }, [setState, defaultValue]);

  const updateState = useCallback(
    (cb: Parameters<Updater<TState>>[0]) => {
      setState((s) => produce(s, cb));
    },
    [setState]
  );
  return { state, setState, clearState, updateState };
}
